import BigNumber from 'bignumber.js';

import { OptionType } from '#/api/markets';

import { MarketsStoreView } from '#/features/perpetuals/market-info/markets-context';
import { MarketsSummaryStoreView } from '#/features/perpetuals/market-info/markets-summary-context';
import { sortBigNumbers } from '#/features/shared/sorting';

export default function deriveClosestSymbolsToSpot(
  marketsStore: MarketsStoreView,
  marketsSummaryStore: MarketsSummaryStoreView,
) {
  // Derive base currencies for the option markets
  // {'BTC': null, 'ETH': null, 'SOL': null}
  const baseCurrencies = marketsStore
    .getAllMarkets()
    .filter((market) => market.asset_kind === 'PERP_OPTION')
    .reduce<Record<string, null>>((acc, market) => {
      acc[market.base_currency] = null;
      return acc;
    }, {});

  // Derive underlying prices for the option markets
  // {'BTC': 84100, 'ETH': 24100, 'SOL': 102}
  const baseCurrenciesWithPrices = Object.entries(baseCurrencies).reduce<
    Record<string, BigNumber | null>
  >((acc, [baseCurrency]) => {
    const underlyingPrice = marketsSummaryStore.getMarketSummary(
      `${baseCurrency}-USD-PERP`,
    )?.underlying_price;
    acc[baseCurrency] = underlyingPrice ?? null;
    return acc;
  }, {});

  // Derive closest symbols to spot for the option markets
  // {
  //   BTC: {
  //     CALL: 'BTC-USD-84000-C',
  //     PUT: 'BTC-USD-83000-P',
  //   },
  //   ETH: {
  //     CALL: 'ETH-USD-2400-C',
  //     PUT: 'ETH-USD-2400-P',
  //   },
  //   SOL: {
  //     CALL: 'SOL-USD-95-C',
  //     PUT: 'SOL-USD-100-P',
  //   },
  // };
  const closestSymbolToSpotMap: Record<
    string,
    Record<OptionType, string | null>
  > = Object.fromEntries(
    Object.keys(baseCurrencies).map((baseCurrency) => [
      baseCurrency,
      {
        CALL: null,
        PUT: null,
      },
    ]),
  );
  Object.entries(baseCurrenciesWithPrices).forEach(
    ([baseCurrency, markPrice]) => {
      if (markPrice == null) return;

      const markets = marketsStore
        .getAllMarkets()
        .filter(
          (market) =>
            market.asset_kind === 'PERP_OPTION' &&
            market.base_currency === baseCurrency,
        );
      const marketsWithMarkDiff = markets.map((market) => ({
        symbol: market.symbol,
        option_type: market.option_type,
        diff: market.strike_price?.minus(markPrice).abs() ?? null,
      }));
      const sortedMarketsWithMarkDiff = marketsWithMarkDiff.sort((a, b) =>
        sortBigNumbers(a.diff, b.diff),
      );
      const closestCall = sortedMarketsWithMarkDiff.find(
        (market) => market.option_type! === 'CALL',
      );
      const closestPut = sortedMarketsWithMarkDiff.find(
        (market) => market.option_type! === 'PUT',
      );

      closestSymbolToSpotMap[baseCurrency] = {
        CALL: closestCall?.symbol ?? null,
        PUT: closestPut?.symbol ?? null,
      };
    },
  );

  return closestSymbolToSpotMap;
}
